import { Fragment } from "react";
import type { ReactNode } from "react";
import type { HomeContent, ThreadBlock } from "../content/home";
import { Editable } from "../edit";
import { useSiteLink } from "../link";

// The three decorative gradient icons in the "quality" glass row are fixed
// visual assets, not editable copy, so they live here and are matched to
// features by position.
const GLASS_ICONS: ReactNode[] = [
  <svg className="ico" viewBox="0 0 40 40" fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="g1" x1="0" y1="0" x2="40" y2="40">
        <stop stopColor="#7b62e0" />
        <stop offset=".55" stopColor="#eb5f72" />
        <stop offset="1" stopColor="#eb5b21" />
      </linearGradient>
    </defs>
    <path d="M20 4v32M4 20h32M8.7 8.7l22.6 22.6M31.3 8.7 8.7 31.3" stroke="url(#g1)" strokeWidth="3" strokeLinecap="round" />
  </svg>,
  <svg className="ico" viewBox="0 0 40 40" fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="g2" x1="0" y1="40" x2="40" y2="0">
        <stop stopColor="#7b62e0" />
        <stop offset="1" stopColor="#eb5b21" />
      </linearGradient>
    </defs>
    <path d="M8 32 32 8M14 8h18v18" stroke="url(#g2)" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>,
  <svg className="ico" viewBox="0 0 40 40" fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="g3" x1="0" y1="40" x2="40" y2="0">
        <stop stopColor="#7b62e0" />
        <stop offset="1" stopColor="#eb5b21" />
      </linearGradient>
    </defs>
    <path d="M9 32v-7M17 32V19M25 32V12M33 32V6" stroke="url(#g3)" strokeWidth="4" strokeLinecap="round" />
  </svg>,
];

function ThreadItem({ block, path }: { block: ThreadBlock; path: string }) {
  switch (block.kind) {
    case "msg":
      return (
        <p className={`msg ${block.dir}`} dir="rtl">
          <Editable as="span" path={`${path}.text`} value={block.text} />
          <span className="meta" dir="ltr">
            {block.time}
          </span>
        </p>
      );
    case "chips":
      return (
        <div className="chips" dir="rtl">
          {block.items.map((chip, i) => (
            <Editable as="span" className="chip" key={i} path={`${path}.items.${i}`} value={chip} />
          ))}
        </div>
      );
    case "handoff":
      return <Editable as="p" className="handoff" path={`${path}.text`} value={block.text} />;
  }
}

export function HomeSections({ content }: { content: HomeContent }) {
  const Link = useSiteLink();
  const c = content;
  return (
    <>
      <section className="hero">
        <div className="hero-art" aria-hidden="true"></div>
        <div className="wrap">
          <div className="hero-copy">
            <Editable as="p" className="eyebrow" path="hero.eyebrow" value={c.hero.eyebrow} />
            <h1>
              <Editable as="span" path="hero.headingLead" value={c.hero.headingLead} />{" "}
              <Editable as="span" className="grad-text" path="hero.headingHighlight" value={c.hero.headingHighlight} />
            </h1>
            <Editable as="p" className="lede" path="hero.lede" value={c.hero.lede} singleLine={false} />
            <div className="hero-ctas">
              {c.hero.ctas.map((cta, i) => (
                <Link key={i} className={`btn btn-${cta.variant}`} to={cta.to}>
                  <Editable as="span" path={`hero.ctas.${i}.label`} value={cta.label} />
                  {cta.arrow && (
                    <>
                      {" "}
                      <span className="arrow" aria-hidden="true">
                        →
                      </span>
                    </>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="backers">
        <div className="wrap">
          <Editable as="span" className="label" path="backers.label" value={c.backers.label} />
          {c.backers.partners.map((p, i) => (
            <Fragment key={i}>
              {i > 0 && (
                <span className="x" aria-hidden="true">
                  ×
                </span>
              )}
              <a className="backer" href={p.href} target="_blank" rel="noopener">
                <Editable as="strong" path={`backers.partners.${i}.name`} value={p.name} />
                <Editable as="span" path={`backers.partners.${i}.description`} value={p.description} />
              </a>
            </Fragment>
          ))}
        </div>
      </div>

      <section className="section">
        <div className="wrap">
          <div className="section-head">
            <Editable as="p" className="eyebrow" path="whyQuintara.eyebrow" value={c.whyQuintara.eyebrow} />
            <Editable as="h2" path="whyQuintara.heading" value={c.whyQuintara.heading} />
            <Editable as="p" className="lede" path="whyQuintara.lede" value={c.whyQuintara.lede} singleLine={false} />
          </div>
          <div className="figures">
            {c.whyQuintara.figures.map((f, i) => (
              <div className="figure" key={i}>
                <Editable as="b" path={`whyQuintara.figures.${i}.value`} value={f.value} />
                <Editable as="span" path={`whyQuintara.figures.${i}.caption`} value={f.caption} />
              </div>
            ))}
          </div>
          <p className="footnote"></p>
        </div>
      </section>

      <hr className="divider" />

      <section className="section">
        <div className="wrap">
          <div className="section-head">
            <Editable as="p" className="eyebrow" path="migrationPath.eyebrow" value={c.migrationPath.eyebrow} />
            <Editable as="h2" path="migrationPath.heading" value={c.migrationPath.heading} />
            <Editable as="p" className="lede" path="migrationPath.lede" value={c.migrationPath.lede} singleLine={false} />
          </div>
          <ol className="path">
            {c.migrationPath.steps.map((s, i) => (
              <li key={i}>
                <Editable as="h3" path={`migrationPath.steps.${i}.title`} value={s.title} />
                <Editable as="p" path={`migrationPath.steps.${i}.description`} value={s.description} singleLine={false} />
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="wrap split">
          <div className="copy">
            <Editable as="p" className="eyebrow" path="arabicAi.eyebrow" value={c.arabicAi.eyebrow} />
            <Editable as="h2" path="arabicAi.heading" value={c.arabicAi.heading} />
            <Editable as="p" className="lede" path="arabicAi.lede" value={c.arabicAi.lede} singleLine={false} />
            <ul className="checks">
              {c.arabicAi.checks.map((ck, i) => (
                <li key={i}>
                  <span>
                    <Editable as="b" path={`arabicAi.checks.${i}.boldLead`} value={ck.boldLead} />{" "}
                    <Editable as="span" path={`arabicAi.checks.${i}.text`} value={ck.text} />
                  </span>
                </li>
              ))}
            </ul>
            <p>
              <Link className="text-link" to={c.arabicAi.linkTo}>
                <Editable as="span" path="arabicAi.linkText" value={c.arabicAi.linkText} />{" "}
                <span className="arrow" aria-hidden="true">
                  →
                </span>
              </Link>
            </p>
          </div>
          <div className="stage">
            <div className="phone" role="img" aria-label={c.arabicAi.phone.ariaLabel}>
              <div className="phone-top">
                <span className="avatar"></span>
                <div>
                  <Editable as="b" path="arabicAi.phone.businessName" value={c.arabicAi.phone.businessName} />
                  <Editable as="small" path="arabicAi.phone.accountLabel" value={c.arabicAi.phone.accountLabel} />
                </div>
              </div>
              <div className="thread">
                {c.arabicAi.phone.thread.map((block, i) => (
                  <ThreadItem block={block} key={i} path={`arabicAi.phone.thread.${i}`} />
                ))}
              </div>
            </div>
            <Editable as="p" className="stage-caption" path="arabicAi.phone.caption" value={c.arabicAi.phone.caption} />
          </div>
        </div>
      </section>

      <hr className="divider" />

      <section className="section">
        <div className="wrap">
          <div className="section-head">
            <Editable as="p" className="eyebrow" path="whoWeWorkWith.eyebrow" value={c.whoWeWorkWith.eyebrow} />
            <Editable as="h2" path="whoWeWorkWith.heading" value={c.whoWeWorkWith.heading} />
          </div>
          <div className="segments">
            {c.whoWeWorkWith.segments.map((s, i) => (
              <Link key={i} className="segment" to={s.to} style={{ textDecoration: "none" }}>
                <Editable as="span" className="tag" path={`whoWeWorkWith.segments.${i}.tag`} value={s.tag} />
                <Editable as="h3" path={`whoWeWorkWith.segments.${i}.heading`} value={s.heading} />
                <Editable as="p" path={`whoWeWorkWith.segments.${i}.description`} value={s.description} singleLine={false} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <div className="glass-row">
            {c.qualityFeatures.map((f, i) => (
              <div className="glass" key={i}>
                {GLASS_ICONS[i] ?? GLASS_ICONS[0]}
                <Editable as="h3" path={`qualityFeatures.${i}.heading`} value={f.heading} />
                <Editable as="p" path={`qualityFeatures.${i}.description`} value={f.description} singleLine={false} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-band">
        <div className="wrap">
          <Editable as="p" className="eyebrow" path="ctaBand.eyebrow" value={c.ctaBand.eyebrow} />
          <Editable as="h2" path="ctaBand.heading" value={c.ctaBand.heading} />
          <Editable as="p" className="lede" path="ctaBand.lede" value={c.ctaBand.lede} singleLine={false} />
          <Link className="btn btn-primary" to={c.ctaBand.buttonTo}>
            <Editable as="span" path="ctaBand.buttonLabel" value={c.ctaBand.buttonLabel} />{" "}
            <span className="arrow" aria-hidden="true">
              →
            </span>
          </Link>
        </div>
      </section>
    </>
  );
}
